/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tbwAuthorizationService from '../../services/TbwAuthorizationService';
import USER_ACTIONS_TBW from '../../services/permissions/constants/actionsTutorialBuilderWeb';

/** Tutorial create capability; re-fetched when locale or current app changes. */
export interface TutorialCreateState {
    allowed: boolean;
    loading: boolean;
    error: string | null;
    /**
     * Cache key for the (locale, applications) combination this capability was last fetched for.
     * Format: "<locale>|<sap_code1>,<sap_code2>,...". null when not yet fetched.
     * Apps are checked separately because a user may have author/curator rights on one app but not another.
     */
    fetchedFor: string | null;
}

/** Per-app `MANAGE_OWN_TUTORIAL` decisions; drives the Create New Tutorial picker. */
export interface TutorialCreatableAppsState {
    appNames: string[];
    loading: boolean;
    error: string | null;
    fetchedFor: string | null;
}

/** Which TBW dashboard sections (groups) the user can list; re-fetched when locale or app changes. */
export interface DashboardSectionsState {
    canListAsAuthor: boolean;
    canListAsCurator: boolean;
    canListAsQE: boolean;
    canListAsLocQE: boolean;
    canListAsProduction: boolean;
    canListAsReadonly: boolean;
    loading: boolean;
    error: string | null;
    /**
     * Cache key for the (locale, applications) combination this state was fetched for.
     * Format: "<locale>|<sap_code1>,<sap_code2>,...". null when not yet fetched.
     */
    fetchedFor: string | null;
}

/** All 13 workflow flags for a single tutorial row. */
export interface TutorialPermissions {
    canSubmitAsAuthor: boolean;
    canSubmitAsCurator: boolean;
    canApproveAsCurator: boolean;
    canApproveAsQE: boolean;
    canApproveAsLocQE: boolean;
    canRejectAsCurator: boolean;
    canRejectAsQE: boolean;
    canAssignToCurator: boolean;
    canMoveToReview: boolean;
    canCreateNewVersion: boolean;
    canRefreshLoc: boolean;
    canManage: boolean;
    canRead: boolean;
}

/**
 * Per-row TBW tutorial permissions keyed by `${aem_id}%#%${status}`. Cache is incrementally
 * populated: each entry tracks which TutorialPermissions flags have actually been resolved
 * by Cedar in `knownKeys`, so a partial fetch (e.g. dashboard hover asking for 2 flags) can
 * later be supplemented by a full fetch (e.g. editor footer asking for all 13). Readers must
 * check `knownKeys[key]` covers what they need before trusting `permissions[key]` — flags
 * outside `knownKeys` are still at their default `false`.
 */
export interface TutorialRowPermissionsState {
    permissions: Record<string, TutorialPermissions>;
    knownKeys: Record<string, Array<keyof TutorialPermissions>>;
}

const initialTutorialCreate: TutorialCreateState = {
    allowed: false,
    loading: false,
    error: null,
    fetchedFor: null,
};

const initialTutorialCreatableApps: TutorialCreatableAppsState = {
    appNames: [],
    loading: false,
    error: null,
    fetchedFor: null,
};

const initialDashboardSections: DashboardSectionsState = {
    canListAsAuthor: false,
    canListAsCurator: false,
    canListAsQE: false,
    canListAsLocQE: false,
    canListAsProduction: false,
    canListAsReadonly: false,
    loading: false,
    error: null,
    fetchedFor: null,
};

/**
 * Stable cache key for a (locale, applications) combination.
 *
 * Uses `fi_code` (not `sap_code`) because some products share a sap_code — e.g. Photoshop
 * Desktop and Photoshop Web both have sap_code "PHSP", but distinct fi_codes
 * ("photoshop" vs "photoshop_web"). Keying on sap_code alone caused tab switches between
 * those two to short-circuit the thunk, leaving stale dashboard sections rendered for the
 * other app.
 */
export const localeAppsCacheKey = (
    locale: string,
    applications: Array<{ sap_code: string; fi_code: string }>,
): string => {
    const fiCodes = applications
        .map((a) => a.fi_code)
        .slice()
        .sort()
        .join(',');
    return `${locale}|${fiCodes}`;
};

const initialTutorialRowPermissions: TutorialRowPermissionsState = {
    permissions: {},
    knownKeys: {},
};

export interface TbwAuthorizationState {
    tutorialCreate: TutorialCreateState;
    tutorialCreatableApps: TutorialCreatableAppsState;
    dashboardSections: DashboardSectionsState;
    tutorialRowPermissions: TutorialRowPermissionsState;
}

const initialState: TbwAuthorizationState = {
    tutorialCreate: initialTutorialCreate,
    tutorialCreatableApps: initialTutorialCreatableApps,
    dashboardSections: initialDashboardSections,
    tutorialRowPermissions: initialTutorialRowPermissions,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

type TutorialCreateResult = {
    allowed: boolean;
    fetchedFor: string;
};

type TutorialCreatableAppsResult = {
    appNames: string[];
    fetchedFor: string;
};

type DashboardSectionsResult = {
    canListAsAuthor: boolean;
    canListAsCurator: boolean;
    canListAsQE: boolean;
    canListAsLocQE: boolean;
    canListAsProduction: boolean;
    canListAsReadonly: boolean;
    fetchedFor: string;
};

/**
 * Fetch TBW tutorial create capability for the given (locale, applications) combination.
 * Gates the "Create New Tutorial" button. A user may have author/curator rights on one app
 * but not another, so we re-check whenever the current app or locale changes.
 */
export const fetchTutorialCreateCapability = createAsyncThunk<
    TutorialCreateResult,
    { locale: string; applications: Array<{ sap_code: string; fi_code: string }> },
    { state: { tbwAuthorization: TbwAuthorizationState } }
>(
    'tbwAuthorization/fetchTutorialCreateCapability',
    async ({ locale, applications }) => {
        const result = await tbwAuthorizationService.checkCapabilities(
            [USER_ACTIONS_TBW.MANAGE_OWN_TUTORIAL],
            { locale, applications },
        );
        return {
            allowed: result.permissions[USER_ACTIONS_TBW.MANAGE_OWN_TUTORIAL] ?? false,
            fetchedFor: localeAppsCacheKey(locale, applications),
        };
    },
    {
        condition: ({ locale, applications }, { getState }) => {
            const key = localeAppsCacheKey(locale, applications);
            return getState().tbwAuthorization.tutorialCreate.fetchedFor !== key;
        },
    },
);

/** Per-app `MANAGE_OWN_TUTORIAL` check; pass all TBW products to populate the creatable set. */
export const fetchTutorialCreatableApps = createAsyncThunk<
    TutorialCreatableAppsResult,
    { locale: string; applications: Array<{ sap_code: string; fi_code: string }> },
    { state: { tbwAuthorization: TbwAuthorizationState } }
>(
    'tbwAuthorization/fetchTutorialCreatableApps',
    async ({ locale, applications }) => {
        const perApp = await tbwAuthorizationService.checkTutorialActionPerApp(
            USER_ACTIONS_TBW.MANAGE_OWN_TUTORIAL,
            locale,
            applications,
        );
        const appNames = Object.entries(perApp)
            .filter(([, allowed]) => allowed)
            .map(([appName]) => appName);
        return { appNames, fetchedFor: localeAppsCacheKey(locale, applications) };
    },
    {
        condition: ({ locale, applications }, { getState }) => {
            const key = localeAppsCacheKey(locale, applications);
            return getState().tbwAuthorization.tutorialCreatableApps.fetchedFor !== key;
        },
    },
);

/**
 * Fetch which dashboard sections the user can see (list-tutorials-as-*).
 * Cedar policies for these query actions evaluate against the user's locale + applications,
 * so the caller must supply at least one application. Skips the call if already fetched.
 */
export const fetchDashboardSections = createAsyncThunk<
    DashboardSectionsResult,
    { locale: string; applications: Array<{ sap_code: string; fi_code: string }> },
    { state: { tbwAuthorization: TbwAuthorizationState } }
>(
    'tbwAuthorization/fetchDashboardSections',
    async ({ locale, applications }) => {
        const actions = [
            USER_ACTIONS_TBW.LIST_TUTORIALS_AS_AUTHOR,
            USER_ACTIONS_TBW.LIST_TUTORIALS_AS_CURATOR,
            USER_ACTIONS_TBW.LIST_TUTORIALS_AS_QE,
            USER_ACTIONS_TBW.LIST_TUTORIALS_AS_LOCQE,
            USER_ACTIONS_TBW.LIST_TUTORIALS_AS_PRODUCTION,
            USER_ACTIONS_TBW.LIST_TUTORIALS_AS_READONLY,
        ];
        const result = await tbwAuthorizationService.checkCapabilities(actions, {
            locale,
            applications,
        });
        return {
            canListAsAuthor: result.permissions[USER_ACTIONS_TBW.LIST_TUTORIALS_AS_AUTHOR] ?? false,
            canListAsCurator: result.permissions[USER_ACTIONS_TBW.LIST_TUTORIALS_AS_CURATOR] ?? false,
            canListAsQE: result.permissions[USER_ACTIONS_TBW.LIST_TUTORIALS_AS_QE] ?? false,
            canListAsLocQE: result.permissions[USER_ACTIONS_TBW.LIST_TUTORIALS_AS_LOCQE] ?? false,
            canListAsProduction: result.permissions[USER_ACTIONS_TBW.LIST_TUTORIALS_AS_PRODUCTION] ?? false,
            canListAsReadonly: result.permissions[USER_ACTIONS_TBW.LIST_TUTORIALS_AS_READONLY] ?? false,
            fetchedFor: localeAppsCacheKey(locale, applications),
        };
    },
    {
        condition: ({ locale, applications }, { getState }) => {
            const key = localeAppsCacheKey(locale, applications);
            return getState().tbwAuthorization.dashboardSections.fetchedFor !== key;
        },
    },
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const tbwAuthorizationSlice = createSlice({
    name: 'tbwAuthorization',
    initialState,
    reducers: {
        /**
         * Merge a partial permission resolution into the row cache. `permissions` is the
         * Cedar response and `fetchedKeys` lists which flags inside it are authoritative
         * (others should be ignored — they may still be at default `false`). The reducer
         * unions `fetchedKeys` into the entity's `knownKeys`, and updates only those flags
         * in the entry's `permissions` object. This lets a dashboard hover (subset) and a
         * footer mount (full) cumulatively populate the same entry.
         */
        setTutorialPermission(
            state,
            action: PayloadAction<{
                key: string;
                permissions: TutorialPermissions;
                fetchedKeys: Array<keyof TutorialPermissions>;
            }>,
        ) {
            const { key, permissions, fetchedKeys } = action.payload;
            const existingPerms = state.tutorialRowPermissions.permissions[key];
            const existingKnown = state.tutorialRowPermissions.knownKeys[key] ?? [];

            const mergedPerms: TutorialPermissions = existingPerms
                ? { ...existingPerms }
                : {
                      canSubmitAsAuthor: false,
                      canSubmitAsCurator: false,
                      canApproveAsCurator: false,
                      canApproveAsQE: false,
                      canApproveAsLocQE: false,
                      canRejectAsCurator: false,
                      canRejectAsQE: false,
                      canAssignToCurator: false,
                      canMoveToReview: false,
                      canCreateNewVersion: false,
                      canRefreshLoc: false,
                      canManage: false,
                      canRead: false,
                  };
            fetchedKeys.forEach((k) => {
                mergedPerms[k] = permissions[k];
            });

            const knownSet = new Set<keyof TutorialPermissions>(existingKnown);
            fetchedKeys.forEach((k) => knownSet.add(k));

            state.tutorialRowPermissions.permissions[key] = mergedPerms;
            state.tutorialRowPermissions.knownKeys[key] = Array.from(knownSet);
        },
        clearTbwAuthorization() {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTutorialCreateCapability.pending, (state) => {
                state.tutorialCreate.loading = true;
                state.tutorialCreate.error = null;
            })
            .addCase(fetchTutorialCreateCapability.fulfilled, (state, action) => {
                state.tutorialCreate.allowed = action.payload.allowed;
                state.tutorialCreate.fetchedFor = action.payload.fetchedFor;
                state.tutorialCreate.loading = false;
                state.tutorialCreate.error = null;
            })
            .addCase(fetchTutorialCreateCapability.rejected, (state, action) => {
                state.tutorialCreate.loading = false;
                state.tutorialCreate.error = action.error.message ?? 'Failed to fetch tutorial create capability';
            });

        builder
            .addCase(fetchTutorialCreatableApps.pending, (state) => {
                state.tutorialCreatableApps.loading = true;
                state.tutorialCreatableApps.error = null;
            })
            .addCase(fetchTutorialCreatableApps.fulfilled, (state, action) => {
                state.tutorialCreatableApps.appNames = action.payload.appNames;
                state.tutorialCreatableApps.fetchedFor = action.payload.fetchedFor;
                state.tutorialCreatableApps.loading = false;
                state.tutorialCreatableApps.error = null;
            })
            .addCase(fetchTutorialCreatableApps.rejected, (state, action) => {
                state.tutorialCreatableApps.loading = false;
                state.tutorialCreatableApps.error = action.error.message ?? 'Failed to fetch creatable apps';
            });

        builder
            .addCase(fetchDashboardSections.pending, (state) => {
                state.dashboardSections.loading = true;
                state.dashboardSections.error = null;
            })
            .addCase(fetchDashboardSections.fulfilled, (state, action) => {
                state.dashboardSections.canListAsAuthor = action.payload.canListAsAuthor;
                state.dashboardSections.canListAsCurator = action.payload.canListAsCurator;
                state.dashboardSections.canListAsQE = action.payload.canListAsQE;
                state.dashboardSections.canListAsLocQE = action.payload.canListAsLocQE;
                state.dashboardSections.canListAsProduction = action.payload.canListAsProduction;
                state.dashboardSections.canListAsReadonly = action.payload.canListAsReadonly;
                state.dashboardSections.loading = false;
                state.dashboardSections.error = null;
                state.dashboardSections.fetchedFor = action.payload.fetchedFor;
            })
            .addCase(fetchDashboardSections.rejected, (state, action) => {
                state.dashboardSections.loading = false;
                state.dashboardSections.error = action.error.message ?? 'Failed to fetch dashboard sections';
            });
    },
});

export const { setTutorialPermission, clearTbwAuthorization } = tbwAuthorizationSlice.actions;
export default tbwAuthorizationSlice.reducer;
